import { message, Spin } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PlusOutlined } from "@ant-design/icons";
import { SearchInput } from "../../../../shared/base-components/Search/Search";
import ProjectService, {
  PagedResponse,
  ProjectDto,
} from "../../services/ProjectService";
import ClientService, { ClientDto } from "../../../ClientView/services/ClientService";
import { CreateProjectModal } from "./CreateProjectModal";
import styles from "./ProjectList.module.css";

const projectService = new ProjectService();
const clientService = new ClientService();

export const ProjectList: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [clients, setClients] = useState<ClientDto[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>("");

  useEffect(() => {
    clientService.getAllClients().then((result) => {
      setClients(result);
      if (result.length > 0) setSelectedClientId(result[0].id);
    }).catch(() => {});
  }, []);

  const fetchProjects = useCallback(async () => {
    setIsLoading(true);
    try {
      const response: PagedResponse<ProjectDto[]> =
        await projectService.getProjects({
          itemsPerPage: 50,
          startIndex: 0,
          searchValue: searchValue,
        });
      setProjects(response.items);
      setTotal(response.totalCount);
    } catch (error) {
      message.error("Failed to load projects");
    } finally {
      setIsLoading(false);
    }
  }, [searchValue]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const openProject = (project: ProjectDto) => {
    navigate(`/calculations/project/${project.id}`);
  };

  const formatCount = (count: number) => {
    return count.toLocaleString("en-US");
  };

  return (
    <div className={styles.glassWrapper}>
      {/* Page header */}
      <div className={styles.pageHeader}>
        <div className={styles.pageTitle}>Projects</div>
        <div className={styles.pageSubtitle}>Select a project to view its deliverables</div>
      </div>

      {/* Toolbar */}
      <div className={styles.toolbar}>
        <button
          className={styles.btnCreate}
          onClick={() => setShowCreate(true)}
        >
          <PlusOutlined style={{ marginRight: 6 }} />
          Create Project
        </button>
        <div className={styles.clientLabel}>Client:</div>
        <select
          className={styles.clientDropdown}
          value={selectedClientId}
          onChange={(e) => setSelectedClientId(e.target.value)}
        >
          {clients.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <SearchInput
          onSearch={(value) => setSearchValue(value)}
          placeholder="Search Projects"
        />
        <div className={styles.toolbarSpacer} />
        <span className={styles.projectCount}>
          {formatCount(total)} PROJECTS
        </span>
      </div>

      {/* Column Headers */}
      <div className={styles.listHeader}>
        <span className={styles.colProject}>Project</span>
        <span className={styles.colEngagement}>Engagement</span>
        <span className={styles.colEntity}>Entity</span>
        <span className={styles.colTaxYear}>Tax Year</span>
      </div>

      {/* Project Rows */}
      <div className={styles.listContainer}>
        {isLoading ? (
          <div className={styles.loadingState}>
            <Spin />
          </div>
        ) : projects.length === 0 ? (
          <div className={styles.emptyState}>No projects found</div>
        ) : (
          projects.map((project) => (
            <div
              key={project.id}
              className={styles.projectRow}
              onClick={() => openProject(project)}
            >
              <span className={styles.colProject}>{project.name}</span>
              <span className={styles.colEngagement}>
                {project.engagementType}
              </span>
              <span className={styles.colEntity}>{project.entityName}</span>
              <span className={styles.colTaxYear}>{project.taxYear}</span>
            </div>
          ))
        )}
      </div>

      {/* Create Project Modal */}
      <CreateProjectModal
        visible={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(projectId) => {
          setShowCreate(false);
          navigate(`/calculations/project/${projectId}/workspace`);
        }}
      />
    </div>
  );
};
